import React, { useEffect, useMemo, useState } from "react";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import Field from "../../components/ui/Field";
import Input from "../../components/ui/Input";
import PageHeader from "../../components/ui/PageHeader";
import Textarea from "../../components/ui/Textarea";
import Switch from "../../components/ui/Switch";
import {
    createSeoRequest,
    getSeoRequest,
    updateSeoRequest,
} from "../../services/api/content.api";
import { SeoItem } from "../../services/types/content.types";

interface FormState {
    url: string;
    titleRu: string;
    titleEn: string;
    titleDe: string;
    titleUa: string;
    h1Ru: string;
    h1En: string;
    h1De: string;
    h1Ua: string;
    descriptionRu: string;
    descriptionEn: string;
    descriptionDe: string;
    descriptionUa: string;
    isActive: boolean;
}

const initialFormState: FormState = {
    url: "",
    titleRu: "",
    titleEn: "",
    titleDe: "",
    titleUa: "",
    h1Ru: "",
    h1En: "",
    h1De: "",
    h1Ua: "",
    descriptionRu: "",
    descriptionEn: "",
    descriptionDe: "",
    descriptionUa: "",
    isActive: true,
};

const SeoPage: React.FC = () => {
    const [items, setItems] = useState<SeoItem[]>([]);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [formState, setFormState] = useState<FormState>(initialFormState);
    const [searchValue, setSearchValue] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [pageError, setPageError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const filteredItems = useMemo(() => {
        const query = searchValue.trim().toLowerCase();

        if (!query) {
            return items;
        }

        return items.filter((item) => item.url.toLowerCase().includes(query));
    }, [items, searchValue]);

    const loadData = async () => {
        setIsLoading(true);
        setPageError(null);

        try {
            const response = await getSeoRequest();
            setItems(response);
        } catch (error) {
            setPageError(error instanceof Error ? error.message : "Не удалось загрузить SEO записи");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        void loadData();
    }, []);

    const resetForm = () => {
        setSelectedId(null);
        setFormState(initialFormState);
        setFormError(null);
        setSuccessMessage(null);
    };

    const handleSelect = (item: SeoItem) => {
        setSelectedId(item.id);
        setFormState({
            url: item.url,
            titleRu: item.title_ru ?? "",
            titleEn: item.title_en ?? "",
            titleDe: item.title_de ?? "",
            titleUa: item.title_ua ?? "",
            h1Ru: item.h1_ru ?? "",
            h1En: item.h1_en ?? "",
            h1De: item.h1_de ?? "",
            h1Ua: item.h1_ua ?? "",
            descriptionRu: item.description_ru ?? "",
            descriptionEn: item.description_en ?? "",
            descriptionDe: item.description_de ?? "",
            descriptionUa: item.description_ua ?? "",
            isActive: item.is_active,
        });
        setFormError(null);
        setSuccessMessage(null);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setFormError(null);
        setSuccessMessage(null);

        if (!formState.url.trim()) {
            setFormError("URL обязателен.");
            return;
        }

        setIsSaving(true);

        try {
            const payload = {
                url: formState.url.trim(),
                title_ru: formState.titleRu.trim() || null,
                title_en: formState.titleEn.trim() || null,
                title_de: formState.titleDe.trim() || null,
                title_ua: formState.titleUa.trim() || null,
                h1_ru: formState.h1Ru.trim() || null,
                h1_en: formState.h1En.trim() || null,
                h1_de: formState.h1De.trim() || null,
                h1_ua: formState.h1Ua.trim() || null,
                description_ru: formState.descriptionRu.trim() || null,
                description_en: formState.descriptionEn.trim() || null,
                description_de: formState.descriptionDe.trim() || null,
                description_ua: formState.descriptionUa.trim() || null,
                is_active: formState.isActive,
            };

            if (selectedId) {
                const updated = await updateSeoRequest(selectedId, payload);
                setItems((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
                handleSelect(updated);
                setSuccessMessage("SEO запись обновлена.");
            } else {
                const created = await createSeoRequest(payload);
                setItems((prev) => [created, ...prev]);
                handleSelect(created);
                setSuccessMessage("SEO запись создана.");
            }
        } catch (error) {
            setFormError(error instanceof Error ? error.message : "Ошибка сохранения SEO");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="page-content">
            <PageHeader
                title="SEO"
                description="Мета-данные страниц сайта: URL, title, H1 и description на трёх языках."
                actions={<Button onClick={resetForm}>Новая SEO запись</Button>}
            />

            <section className="content-split">
                <Card className="content-list-card">
                    <div className="section-toolbar">
                        <div>
                            <h3 className="card-title">SEO записи</h3>
                            <p className="card-text">Управление мета-данными страниц.</p>
                        </div>

                        <div className="section-toolbar-controls">
                            <Input
                                type="text"
                                placeholder="Поиск по URL..."
                                value={searchValue}
                                onChange={(event) => setSearchValue(event.target.value)}
                            />
                        </div>
                    </div>

                    {pageError ? (
                        <div className="form-global-error">{pageError}</div>
                    ) : isLoading ? (
                        <div className="table-skeleton">
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                        </div>
                    ) : filteredItems.length === 0 ? (
                        <div className="empty-block">
                            <h3 className="empty-block-title">Нет SEO записей</h3>
                            <p className="empty-block-text">Создайте первую запись для оптимизации страницы.</p>
                        </div>
                    ) : (
                        <div className="table-wrap">
                            <table className="data-table">
                                <thead>
                                <tr>
                                    <th>URL</th>
                                    <th>Title RU</th>
                                    <th>Статус</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filteredItems.map((item) => (
                                    <tr
                                        key={item.id}
                                        className={item.id === selectedId ? "data-table-row-active" : ""}
                                        onClick={() => handleSelect(item)}
                                    >
                                        <td>{item.url}</td>
                                        <td>{item.title_ru || "—"}</td>
                                        <td>
                        <span
                            className={`status-badge ${
                                item.is_active ? "status-badge-active" : "status-badge-inactive"
                            }`}
                        >
                          {item.is_active ? "Активна" : "Выключена"}
                        </span>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>

                <Card className="content-form-card">
                    <div className="editor-head">
                        <div>
                            <h3 className="card-title">{selectedId ? "Редактирование SEO" : "Новая SEO запись"}</h3>
                            <p className="card-text">Description теперь можно редактировать в визуальном режиме.</p>
                        </div>
                    </div>

                    <form className="editor-form" onSubmit={handleSubmit}>
                        <Field label="URL" htmlFor="seo-url">
                            <Input
                                id="seo-url"
                                value={formState.url}
                                onChange={(event) =>
                                    setFormState((prev) => ({ ...prev, url: event.target.value }))
                                }
                            />
                        </Field>

                        <div className="form-grid form-grid-two-columns">
                            <Field label="Title (RU)" htmlFor="seo-title-ru">
                                <Input
                                    id="seo-title-ru"
                                    value={formState.titleRu}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, titleRu: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Title (EN)" htmlFor="seo-title-en">
                                <Input
                                    id="seo-title-en"
                                    value={formState.titleEn}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, titleEn: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Title (DE)" htmlFor="seo-title-de">
                                <Input
                                    id="seo-title-de"
                                    value={formState.titleDe}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, titleDe: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Title (UA)" htmlFor="seo-title-ua">
                                <Input
                                    id="seo-title-ua"
                                    value={formState.titleUa}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, titleUa: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="H1 (RU)" htmlFor="seo-h1-ru">
                                <Input
                                    id="seo-h1-ru"
                                    value={formState.h1Ru}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, h1Ru: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="H1 (EN)" htmlFor="seo-h1-en">
                                <Input
                                    id="seo-h1-en"
                                    value={formState.h1En}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, h1En: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="H1 (DE)" htmlFor="seo-h1-de">
                                <Input
                                    id="seo-h1-de"
                                    value={formState.h1De}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, h1De: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="H1 (UA)" htmlFor="seo-h1-ua">
                                <Input
                                    id="seo-h1-ua"
                                    value={formState.h1Ua}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, h1Ua: event.target.value }))
                                    }
                                />
                            </Field>
                        </div>

                        <Field label="Description (RU)" htmlFor="seo-description-ru">
                            <Textarea
                                id="seo-description-ru"
                                rows={5}
                                value={formState.descriptionRu}
                                onChange={(event) =>
                                    setFormState((prev) => ({ ...prev, descriptionRu: event.target.value }))
                                }
                            />
                        </Field>

                        <Field label="Description (EN)" htmlFor="seo-description-en">
                            <Textarea
                                id="seo-description-en"
                                rows={5}
                                value={formState.descriptionEn}
                                onChange={(event) =>
                                    setFormState((prev) => ({ ...prev, descriptionEn: event.target.value }))
                                }
                            />
                        </Field>

                        <Field label="Description (DE)" htmlFor="seo-description-de">
                            <Textarea
                                id="seo-description-de"
                                rows={5}
                                value={formState.descriptionDe}
                                onChange={(event) =>
                                    setFormState((prev) => ({ ...prev, descriptionDe: event.target.value }))
                                }
                            />
                        </Field>

                        <Field label="Description (UA)" htmlFor="seo-description-ua">
                            <Textarea
                                id="seo-description-ua"
                                rows={5}
                                value={formState.descriptionUa}
                                onChange={(event) =>
                                    setFormState((prev) => ({ ...prev, descriptionUa: event.target.value }))
                                }
                            />
                        </Field>

                        <div className="form-switch-row">
                            <Switch
                                checked={formState.isActive}
                                onChange={(checked) =>
                                    setFormState((prev) => ({ ...prev, isActive: checked }))
                                }
                                label={formState.isActive ? "SEO запись активна" : "SEO запись выключена"}
                            />
                        </div>

                        {formError ? <div className="form-global-error">{formError}</div> : null}
                        {successMessage ? <div className="form-global-success">{successMessage}</div> : null}

                        <div className="editor-actions">
                            <Button type="submit" disabled={isSaving}>
                                {isSaving ? "Сохраняем..." : selectedId ? "Сохранить изменения" : "Создать запись"}
                            </Button>
                            <Button type="button" variant="secondary" onClick={resetForm}>
                                Очистить форму
                            </Button>
                        </div>
                    </form>
                </Card>
            </section>
        </div>
    );
};

export default SeoPage;
